export const RETAIL_ROLES = {
  CAJERO: "CAJERO",
  ENCARGADO: "ENCARGADO",
  ADMIN: "ADMIN",
  DUENO: "DUEÑO",
};

const ROLE_ALIASES = {
  cashier: RETAIL_ROLES.CAJERO,
  cajero: RETAIL_ROLES.CAJERO,
  seller: RETAIL_ROLES.CAJERO,
  vendedor: RETAIL_ROLES.CAJERO,
  encargado: RETAIL_ROLES.ENCARGADO,
  supervisor: RETAIL_ROLES.ENCARGADO,
  manager: RETAIL_ROLES.ENCARGADO,
  admin: RETAIL_ROLES.ADMIN,
  administrador: RETAIL_ROLES.ADMIN,
  due\u00f1o: RETAIL_ROLES.DUENO,
  dueno: RETAIL_ROLES.DUENO,
  owner: RETAIL_ROLES.DUENO,
  root: RETAIL_ROLES.DUENO,
};

export const PERMISSIONS_BY_ROLE = {
  [RETAIL_ROLES.CAJERO]: {
    tabs: {
      POS: true,
      INVENTARIO: false,
      COMPRAS: false,
      VENTAS: true,
      CLIENTES: true,
      PROMOS: false,
      GROWTH: false,
      REPORTES: false,
      SEGURIDAD: false,
    },
    actions: {
      ANULAR_VENTA: false,
      ABRIR_CONFIG: false,
      GESTIONAR_SEGURIDAD: false,
    },
  },
  [RETAIL_ROLES.ENCARGADO]: {
    tabs: {
      POS: true,
      INVENTARIO: true,
      COMPRAS: false,
      VENTAS: true,
      CLIENTES: true,
      PROMOS: false,
      GROWTH: false,
      REPORTES: true,
      SEGURIDAD: false,
    },
    actions: {
      ANULAR_VENTA: true,
      ABRIR_CONFIG: false,
      GESTIONAR_SEGURIDAD: false,
    },
  },
  [RETAIL_ROLES.ADMIN]: {
    tabs: {
      POS: true,
      INVENTARIO: true,
      COMPRAS: true,
      VENTAS: true,
      CLIENTES: true,
      PROMOS: true,
      GROWTH: true,
      REPORTES: true,
      SEGURIDAD: true,
    },
    actions: {
      ANULAR_VENTA: true,
      ABRIR_CONFIG: true,
      GESTIONAR_SEGURIDAD: true,
    },
  },
  [RETAIL_ROLES.DUENO]: {
    tabs: {
      POS: true,
      INVENTARIO: true,
      COMPRAS: true,
      VENTAS: true,
      CLIENTES: true,
      PROMOS: true,
      GROWTH: true,
      REPORTES: true,
      SEGURIDAD: true,
    },
    actions: {
      ANULAR_VENTA: true,
      ABRIR_CONFIG: true,
      GESTIONAR_SEGURIDAD: true,
    },
  },
};

export const ASSIGNABLE_NON_ADMIN_ROLES = [
  RETAIL_ROLES.CAJERO,
  RETAIL_ROLES.ENCARGADO,
  RETAIL_ROLES.DUENO,
];

export function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function normalizeRetailRole(rawRole) {
  const normalized = String(rawRole || "")
    .trim()
    .toLowerCase();

  if (!normalized) return RETAIL_ROLES.DUENO;
  return ROLE_ALIASES[normalized] || RETAIL_ROLES.DUENO;
}

export function normalizeStoreMembershipRole(rawRole) {
  const normalized = String(rawRole || "").trim().toLowerCase();
  if (!normalized) return null;

  const map = {
    vendedor: RETAIL_ROLES.CAJERO,
    cajero: RETAIL_ROLES.CAJERO,
    encargado: RETAIL_ROLES.ENCARGADO,
    admin: RETAIL_ROLES.ADMIN,
    administrador: RETAIL_ROLES.ADMIN,
    due\u00f1o: RETAIL_ROLES.DUENO,
    dueno: RETAIL_ROLES.DUENO,
    owner: RETAIL_ROLES.DUENO,
  };

  return map[normalized] || null;
}

export function getRetailPermissions(role) {
  return PERMISSIONS_BY_ROLE[normalizeRetailRole(role)] || PERMISSIONS_BY_ROLE[RETAIL_ROLES.DUENO];
}

export function getRetailRoleFromUser(user) {
  if (!user || typeof user !== "object") return RETAIL_ROLES.DUENO;

  const rawRole =
    user.role ||
    user.rol ||
    user.perfil ||
    user.profile ||
    user.user_role ||
    user.type ||
    user.tipo;

  return normalizeRetailRole(rawRole);
}

export function resolveRetailAccess({ user, isAuthenticated, securityPolicy, storeRole }) {
  if (!isAuthenticated) {
    return {
      allowed: false,
      reason: "Debes iniciar sesión para acceder al POS",
      role: null,
      isOwner: false,
      email: "",
    };
  }

  const email = normalizeEmail(user?.email || user?.username || user?.correo || user?.mail);
  if (!email) {
    return {
      allowed: false,
      reason: "No se pudo identificar el correo autenticado del usuario",
      role: null,
      isOwner: false,
      email: "",
    };
  }

  const ownerEmail = normalizeEmail(securityPolicy?.ownerEmail || email);
  const isOwner = email === ownerEmail;
  const allowedEmails = Array.isArray(securityPolicy?.allowedEmails)
    ? securityPolicy.allowedEmails.map(normalizeEmail).filter(Boolean)
    : [];
  const roleByEmail = securityPolicy?.roleByEmail || {};

  const membershipRole = normalizeStoreMembershipRole(storeRole);
  if (membershipRole) {
    return {
      allowed: true,
      reason: "",
      role: membershipRole,
      isOwner: membershipRole === RETAIL_ROLES.DUENO,
      email,
      ownerEmail,
    };
  }

  if (!isOwner && !allowedEmails.includes(email)) {
    return {
      allowed: false,
      reason: "Tu correo no está autorizado para entrar a esta tienda",
      role: null,
      isOwner: false,
      email,
    };
  }

  if (isOwner) {
    return {
      allowed: true,
      reason: "",
      role: RETAIL_ROLES.DUENO,
      isOwner: true,
      email,
      ownerEmail,
    };
  }

  const assignedRaw = roleByEmail[email] || RETAIL_ROLES.CAJERO;
  const assignedRole = normalizeRetailRole(assignedRaw);
  const safeRole = assignedRole === RETAIL_ROLES.ADMIN ? RETAIL_ROLES.ENCARGADO : assignedRole;

  return {
    allowed: true,
    reason: "",
    role: safeRole,
    isOwner: false,
    email,
    ownerEmail,
  };
}
